import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  OPPORTUNITY_TYPES, POSTING_PERMISSIONS, APPLICATION_PERMISSIONS,
  TYPE_LABEL, TYPE_COLORS, ROLE_LABEL, postableTypes, applicableTypes,
} from "@/lib/permissions";
import {
  Briefcase, ClipboardList, MapPin, ArrowUpRight, Building2, TrendingUp, Hammer, Megaphone, Sparkles,
} from "lucide-react";
import MediaManager from "@/components/MediaManager";
import VendorProfileEditor from "@/components/VendorProfileEditor";

const PORTAL_META = {
  business_owner: {
    title: "Business Owner Portal",
    icon: Building2,
    tagline: "Post opportunities. Pick partners. Grow your brand.",
    primaryCTA: { label: "Post an opportunity", to: "/opportunities" },
    role: "business_owner",
  },
  investor: {
    title: "Investor Portal",
    icon: TrendingUp,
    tagline: "Source qualified deals. Express interest. Move fast.",
    primaryCTA: { label: "Post investment", to: "/opportunities" },
    role: "investor",
  },
  contractor: {
    title: "Contractor Portal",
    icon: Hammer,
    tagline: "Find contract jobs. Apply with one click.",
    primaryCTA: { label: "Browse contractor jobs", to: "/opportunities" },
    role: "contractor",
  },
  influencer: {
    title: "Influencer Portal",
    icon: Megaphone,
    tagline: "Discover campaigns. Monetize your audience.",
    primaryCTA: { label: "Browse campaigns", to: "/opportunities" },
    role: "influencer",
  },
};

export default function Portal() {
  const { role: roleParam } = useParams();
  const { user } = useAuth();
  const meta = PORTAL_META[roleParam];

  const [opps, setOpps] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!meta) return;
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/opportunities").then(({ data }) => setOpps(data.slice(0, 12))).catch(() => {});
  }, [meta]);

  if (!meta) return <Navigate to="/dashboard" replace />;

  const Icon = meta.icon;
  const postable = postableTypes(meta.role, false);
  const applicable = applicableTypes(meta.role, false);
  const relevantTypes = [...new Set([...postable.map((t) => t.value), ...applicable.map((t) => t.value)])];
  const relevantOpps = opps.filter((o) => relevantTypes.includes(o.type)).slice(0, 6);

  const isYourPortal = user?.role === meta.role || user?.is_admin;

  // Special quick-recruit shortcuts for business_owner
  const BO_RECRUIT = [
    { key: "affiliate", label: "Recruit affiliates" },
    { key: "agent", label: "Recruit agents" },
    { key: "stockist", label: "Recruit stockists" },
    { key: "distributor", label: "Recruit distributors" },
    { key: "vendor", label: "Recruit vendors" },
    { key: "influencer_campaign", label: "Recruit influencers" },
    { key: "contractor_job", label: "Hire contractors" },
    { key: "territory_partner", label: "Recruit territory partners" },
  ];

  return (
    <div className="space-y-6" data-testid={`portal-${meta.role}-page`}>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="tag" data-testid={`portal-tag-${meta.role}`}>
            <span className="w-1.5 h-1.5 bg-[#C8102E]" /> {ROLE_LABEL[meta.role]}
          </div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-3 flex items-center gap-3">
            <Icon className="w-8 h-8 text-[#C8102E]" /> {meta.title}
          </h1>
          <p className="text-zinc-600 mt-1 text-sm">{meta.tagline}</p>
        </div>
        <Link
          to={meta.primaryCTA.to}
          data-testid={`portal-cta-${meta.role}`}
          className="inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#9A0C24] text-white px-4 py-2.5 font-semibold transition-colors text-sm"
        >
          {meta.primaryCTA.label} <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      {!isYourPortal && (
        <div className="border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex items-start gap-3" data-testid="portal-not-your-role">
          <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
          <span>You're viewing the {ROLE_LABEL[meta.role]} portal but your role is <strong>{ROLE_LABEL[user?.role] || "not set"}</strong>. You can change your role from your profile.</span>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card label="Can post" value={postable.length} testid={`portal-stat-post-${meta.role}`} />
        <Card label="Can apply" value={applicable.length} testid={`portal-stat-apply-${meta.role}`} />
        <Card label="Open opportunities" value={stats?.open_opportunities ?? "—"} testid={`portal-stat-open-${meta.role}`} />
        <Card label="My applications" value={stats?.my_applications ?? "—"} testid={`portal-stat-mine-${meta.role}`} />
      </div>

      {meta.role === "business_owner" && isYourPortal && (
        <>
          <div className="border border-zinc-200 bg-white" data-testid="bo-company-profile">
            <div className="p-5 border-b border-zinc-200">
              <div className="overline text-zinc-500">Company profile</div>
              <h3 className="font-display font-black text-xl tracking-tight">Build trust before recruitment</h3>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
              {user?.company_logo ? (
                <img src={user.company_logo} alt="logo" className="w-24 h-24 object-cover border border-zinc-200" />
              ) : (
                <div className="w-24 h-24 border border-dashed border-zinc-300 grid place-items-center text-zinc-400 text-xs">No logo</div>
              )}
              <div className="md:col-span-2 space-y-1">
                <div className="font-display font-black text-lg tracking-tight">{user?.company || "Unnamed company"}</div>
                <div className="text-sm text-zinc-600">{user?.company_industry || "Industry not set"}</div>
                {user?.company_website && (
                  <a href={user.company_website} target="_blank" rel="noreferrer" className="text-sm text-[#C8102E] hover:underline">
                    {user.company_website}
                  </a>
                )}
                <p className="text-sm text-zinc-600 mt-2">
                  {user?.company_description || "Add a description to attract better applicants."}
                </p>
                <Link to="/profile" data-testid="bo-edit-company" className="inline-flex items-center gap-1 mt-3 text-xs uppercase tracking-widest font-semibold text-[#C8102E] hover:underline">
                  Edit company profile →
                </Link>
              </div>
            </div>
          </div>

          <div className="border border-zinc-200 bg-white" data-testid="bo-recruit-tiles-grid">
            <div className="p-5 border-b border-zinc-200">
              <div className="overline text-zinc-500">Quick recruit</div>
              <h3 className="font-display font-black text-xl tracking-tight">Post recruitment in one click</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4">
              {BO_RECRUIT.map((b, i) => (
                <Link
                  key={b.key}
                  to="/opportunities"
                  state={{ preselectType: b.key }}
                  data-testid={`bo-recruit-${b.key}`}
                  className={`p-5 hover:bg-zinc-50 transition-colors ${i % 4 !== 3 ? "border-r" : ""} ${i < 4 ? "border-b" : ""} border-zinc-200`}
                >
                  <div className="overline text-zinc-500">{b.key.replace("_", " ")}</div>
                  <div className="font-display font-black text-sm tracking-tight mt-1">{b.label}</div>
                </Link>
              ))}
            </div>
          </div>

          <div className="border border-zinc-200 bg-white p-5 flex items-start gap-3" data-testid="bo-influencer-tip">
            <span className="w-2 h-2 bg-pink-500 mt-2 shrink-0" />
            <div className="text-sm text-zinc-700">
              Looking for creators? Browse the <Link to="/influencers" className="text-[#C8102E] font-semibold hover:underline">Influencer marketplace</Link> to discover and recruit creators by category and audience.
            </div>
          </div>
        </>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-zinc-200 bg-white">
          <div className="p-5 border-b border-zinc-200">
            <div className="overline text-zinc-500">What you can post</div>
            <h3 className="font-display font-black text-xl tracking-tight">Posting rights</h3>
          </div>
          <div className="p-3 flex flex-wrap gap-2">
            {postable.length === 0 ? (
              <span className="text-sm text-zinc-500 p-3">No posting rights for this role in v1.</span>
            ) : postable.map((t) => (
              <span key={t.value} data-testid={`portal-postable-${t.value}`} className={`text-[10px] uppercase tracking-widest font-semibold border px-2 py-1 ${TYPE_COLORS[t.value]}`}>
                {t.label}
              </span>
            ))}
          </div>
        </div>
        <div className="border border-zinc-200 bg-white">
          <div className="p-5 border-b border-zinc-200">
            <div className="overline text-zinc-500">What you can apply for</div>
            <h3 className="font-display font-black text-xl tracking-tight">Application rights</h3>
          </div>
          <div className="p-3 flex flex-wrap gap-2">
            {applicable.length === 0 ? (
              <span className="text-sm text-zinc-500 p-3">No application rights for this role in v1.</span>
            ) : applicable.map((t) => (
              <span key={t.value} data-testid={`portal-applicable-${t.value}`} className={`text-[10px] uppercase tracking-widest font-semibold border px-2 py-1 ${TYPE_COLORS[t.value]}`}>
                {t.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="border border-zinc-200 bg-white">
        <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="overline text-zinc-500">For you</div>
            <h3 className="font-display font-black text-xl tracking-tight">Relevant opportunities</h3>
          </div>
          <Link to="/opportunities" className="text-xs uppercase tracking-widest font-semibold text-[#C8102E] hover:underline" data-testid={`portal-see-all-${meta.role}`}>
            See all →
          </Link>
        </div>
        <div className="divide-y divide-zinc-100" data-testid={`portal-opps-${meta.role}`}>
          {relevantOpps.length === 0 && (
            <div className="p-6 text-center text-zinc-500 text-sm">No relevant opportunities yet.</div>
          )}
          {relevantOpps.map((o) => (
            <Link
              key={o.opportunity_id}
              to="/opportunities"
              className="flex items-center justify-between p-4 hover:bg-zinc-50"
              data-testid={`portal-opp-${o.opportunity_id}`}
            >
              <div className="min-w-0">
                <div className="font-medium truncate">{o.title}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-[10px] uppercase tracking-widest font-semibold border px-2 py-0.5 ${TYPE_COLORS[o.type]}`}>
                    {TYPE_LABEL[o.type]}
                  </span>
                  {o.location_name && (
                    <span className="text-xs text-zinc-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {o.location_name}
                    </span>
                  )}
                </div>
              </div>
              {Number(o.budget) > 0 && (
                <div className="font-mono text-sm font-semibold whitespace-nowrap pl-3">
                  ${Number(o.budget).toLocaleString()}
                </div>
              )}
            </Link>
          ))}
        </div>
      </div>

      {(meta.role === "business_owner" || user?.is_admin) && (
        <>
          <PostPromoCTA />
          <VendorProfileEditor />
          <MediaManager />
        </>
      )}
    </div>
  );
}

function PostPromoCTA() {
  return (
    <div className="relative overflow-hidden border border-zinc-200" data-testid="portal-post-promo-cta" style={{ background: "linear-gradient(135deg, #09090b 0%, #1f1f23 60%, #C8102E 140%)" }}>
      <div className="absolute -top-12 -right-12 w-56 h-56 opacity-20 blur-3xl rounded-full" style={{ background: "#E0AA3E" }} />
      <div className="relative p-6 lg:p-8 flex flex-col lg:flex-row items-start lg:items-center gap-6">
        <div className="flex-1 text-white">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold px-3 py-1.5 bg-white/10 backdrop-blur border border-white/15">
            <span className="w-1.5 h-1.5 inline-block" style={{ background: "#E0AA3E" }} /> NEW · Opportunity TV
          </div>
          <h3 className="font-display font-black text-2xl lg:text-3xl tracking-tight mt-3">Promote your brand with a 60-second video.</h3>
          <p className="text-white/80 text-sm mt-2 max-w-xl">
            Reach Malaysians on our TikTok-style feed. Recruit agents, drive sales, and grow your team — all from one upload.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 shrink-0">
          <Link to="/post-promo" className="inline-flex items-center justify-center gap-2 bg-white text-zinc-900 px-5 py-3 text-sm font-bold hover:bg-zinc-100" data-testid="portal-post-promo-btn">
            <Sparkles className="w-4 h-4" /> Post Your Video
          </Link>
          <Link to="/tv" className="inline-flex items-center justify-center gap-2 text-white border border-white/30 px-5 py-3 text-sm font-semibold hover:bg-white/10" data-testid="portal-watch-tv-btn">
            Watch TV
          </Link>
        </div>
      </div>
    </div>
  );
}

function Card({ label, value, testid }) {
  return (
    <div className="border border-zinc-200 p-5 bg-white" data-testid={testid}>
      <div className="overline text-zinc-500">{label}</div>
      <div className="font-display font-black text-3xl tracking-tight mt-2">{value}</div>
    </div>
  );
}
